import {
  Add as AddIcon,
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import {
  Collapse,
  Box,
  Typography,
  List,
  ListSubheader,
  InputBase,
  IconButton,
} from "@mui/material";
import { TreeItem, TreeItemProps } from "@mui/x-tree-view/TreeItem";
import { TreeView } from "@mui/x-tree-view/TreeView";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterListIcon from "@mui/icons-material/FilterList";
import { handleMenuDataValue } from "../../assets/utils";

declare module "react" {
  interface CSSProperties {
    "--tree-view-color"?: string;
    "--tree-view-bg-color"?: string;
  }
}

type StyledTreeItemProps = TreeItemProps & {
  labelIcon?: React.ElementType;
  labelInfo?: string;
  labelText: string;
  nodeInfo: MenuItem;
};

export interface MenuItem {
  id: string;
  name: string;
  children?: MenuItem[];
  polyglotKey?: string;
  displayName: string;
  associatedGrid?: boolean;
}

export interface SideMenuProps {
  onChange: (node: MenuItem, parentName?: string) => void;
  menuData?: MenuItem[];
  currentActiveNode?: MenuItem;
  onRevertSelection?: (restoreFn: () => void) => void;
}

const SideMenu: React.FC<SideMenuProps> = ({
  onChange,
  menuData,
  currentActiveNode,
  onRevertSelection,
}) => {
  const [collapse, setCollapse] = useState(true);
  const [keywords, setKeywords] = useState("");
  const [expanded, setExpanded] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [hasInitialized, setHasInitialized] = useState(false);
  const [previousSelected, setPreviousSelected] = useState<string>("");
  const { t } = useTranslation();

  const getAllNodeIds = (nodes: MenuItem[] = []): string[] => {
    let ids: string[] = [];
    for (const node of nodes) {
      ids.push(node.id);
      if (node.children?.length) {
        ids = ids.concat(getAllNodeIds(node.children));
      }
    }
    return ids;
  };

  useEffect(() => {
    if (currentActiveNode) {
      setSelected(currentActiveNode.id);
    }
  }, [currentActiveNode]);

  useEffect(() => {
    // notify parent component how to revert highlight when "No" is clicked
    if (typeof onRevertSelection === "function") {
      onRevertSelection(() => {
        setSelected(previousSelected);
      });
    }
  }, [previousSelected, onRevertSelection]);

  const StyledTreeItem = React.forwardRef(function StyledTreeItem(
  props: StyledTreeItemProps,
  ref: React.Ref<HTMLLIElement>
) {
  const { labelIcon: LabelIcon, labelText, nodeInfo, ...other } = props;

  const findParentName = (allMenuData: MenuItem[], currentNode: MenuItem): string => {
    const findInTree = (nodes: MenuItem[]): string | null => {
      for (const node of nodes) {
        // Found parent
        if (node.children?.some(child => child.id === currentNode.id)) {
          return node.displayName;
        }
        // Recurse into children
        if (node.children?.length) {
          const found = findInTree(node.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    const parentName = findInTree(allMenuData);
    return parentName || ""; // Empty if root node
  };

  const handleClick = () => {
    if (nodeInfo.children?.length) return;
    
    setPreviousSelected(selected);
    setSelected(nodeInfo.id);
    
    // ✅ Find parent name from full menu data
    const parentName = findParentName(languageMenuData, nodeInfo);
    console.log("node details", nodeInfo, "parent:", parentName);
    
    // ✅ Pass both node AND parentName to onChange
    onChange(nodeInfo, parentName);
  };

  return (
    <TreeItem
      label={
        <Box
          onClick={handleClick}
          sx={{
            display: "flex",
            alignItems: "center",
            p: 0.5,
            pr: 0,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
            {labelText}
          </Typography>
          {LabelIcon && (
            <Box component={LabelIcon} color="inherit" sx={{ mr: 1 }} />
          )}
        </Box>
      }
      {...other}
      ref={ref}
    />
  );
});


  const renderTree = (nodes: MenuItem): JSX.Element => (
    <StyledTreeItem
      nodeId={nodes.id}
      labelText={t(nodes.displayName)}
      nodeInfo={nodes}
    >
      {Array.isArray(nodes.children)
        ? nodes.children.map((node) => renderTree(node))
        : null}
    </StyledTreeItem>
  );

  const filterMenu = (
    keywords: string,
    tree: MenuItem[] = [],
    newArr: MenuItem[] = [],
    expandIds: string[] = []
  ): MenuItem[] => {
    if (!(tree.length && keywords)) return tree;

    for (const item of tree) {
      if (item.displayName?.toLowerCase().includes(keywords.toLowerCase())) {
        newArr.push(item);
        continue;
      }
      if (item?.children?.length) {
        const subArr = filterMenu(keywords, item.children);
        if (subArr?.length) {
          newArr.push({ ...item, children: subArr });
          expandIds.push(item.id);
        }
      }
    }
    return newArr;
  };

  const languageMenuData = useMemo(() => {
    const addPolyglotKey = (tree?: MenuItem[]): MenuItem[] => {
      if (!tree) return [];
      return tree.map((node) => {
        const key = handleMenuDataValue(node.name);
        return {
          ...node,
          polyglotKey: key,
          children: node.children ? addPolyglotKey(node.children) : undefined,
        };
      });
    };
    return addPolyglotKey(menuData);
  }, [menuData]);

  useEffect(() => {
    if (!hasInitialized && languageMenuData && languageMenuData.length > 0) {
       // Expand only the first menu item 
      if (languageMenuData[0]?.id) {
        setExpanded([languageMenuData[0].id]);
      }
      
      const findFirstLeaf = (node: MenuItem): MenuItem | null => {
        if (!node.children?.length) return node;
        return findFirstLeaf(node.children[0]);
      };

      const firstLeaf = findFirstLeaf(languageMenuData[0]);
      if (firstLeaf) {
        setSelected(firstLeaf.id);
        onChange(firstLeaf, firstLeaf.polyglotKey);
      }

      setHasInitialized(true);
    }
  }, [languageMenuData, hasInitialized, onChange]);

 const keywordsFilterMenuResult = useMemo(() => {
    const expandedIds: string[] = [];
    const result = filterMenu(keywords, languageMenuData, [], expandedIds);
    // When there are keywords, expand the filtered results
    // When no keywords, keep only the first menu expanded
    setExpanded(keywords ? expandedIds : (languageMenuData[0]?.id ? [languageMenuData[0].id] : []));
    return result;
  }, [keywords, languageMenuData]);

// Use onNodeToggle to update expanded nodes on user interaction
const handleToggle = (_event: React.SyntheticEvent, nodeIds: string[]) => {
  // console.log("TOggling....");
  setExpanded(nodeIds);
};
  return (
    <Box className="sidenavigation-component" sx={{ ml: "-12px", px: 1 }}>
      <List
        className="rightsidesearch-list custom-collapse-menu"
        component="nav"
        aria-labelledby="nested-list-subheader"
        subheader={
          <ListSubheader
            component="div"
            id="nested-list-subheader"
            className="sidenavigation-heading"
          >
            <IconButton type="button">
              {!keywords && <FilterListIcon color="inherit" />}
            </IconButton>
            <InputBase
              placeholder={t("Search")}
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
            />
            <IconButton type="button">
              {!!keywords && (
                <Close
                  onClick={() => {
                    setKeywords("");
                  }}
                />
              )}
            </IconButton>
            <IconButton
              aria-label={collapse ? "Expand menu" : "Collapse menu"}
              onClick={() => {
                setCollapse(!collapse);
              }}
              style={{ marginLeft: "auto" }}
            >
              {collapse ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
            </IconButton>
          </ListSubheader>
        }
      >
        <Collapse
          in={collapse}
          timeout="auto"
          unmountOnExit
          sx={{ flex: 1, overflow: "auto", mt: 1 }}
        >
          <TreeView
            disabledItemsFocusable
            selected={selected}
            expanded={expanded}
            defaultCollapseIcon={<RemoveIcon />}
            defaultExpandIcon={<AddIcon />}
            defaultEndIcon={<div style={{ width: 24 }} />}
            onNodeToggle={handleToggle}
            sx={{
              height: "100%",
              flexGrow: 1,
              maxWidth: 400,
              overflowY: "auto",
                  fontSize:" 0.875rem",
            
              // Remove background highlight for ALL nodes
              "& .MuiTreeItem-content": {
                backgroundColor: "transparent !important",
              },
            
              // Common reset styles for label, icon, expanded parent
              "& .MuiTreeItem-content .MuiTreeItem-label, \
                 .MuiTreeItem-content .MuiTreeItem-iconContainer, \
                 .MuiTreeItem-content.Mui-expanded": {
                color: "black !important",
                backgroundColor: "transparent !important",
                "&:hover": {
                  backgroundColor: "transparent !important",
                  color: "black !important",
                },
              },
            
              // Selected child — blue border + blue text
              "& .MuiCollapse-wrapper .MuiTreeItem-content.Mui-selected": {
                borderLeft: "3px solid #0c66e4",
                color: "#1976d2 !important",
                backgroundColor: "transparent !important",
                borderTopLeftRadius: "0px !important",
                borderBottomLeftRadius: "0px !important",
              },
              //sets blue color to labels inside expanded container
              "& .MuiCollapse-wrapper .MuiTreeItem-content .MuiTreeItem-label:hover": {
                color: "#0c66e4 !important",
                backgroundColor: "transparent !important"
              },
              // sets blue color to slected itesms
              "& .MuiCollapse-wrapper .MuiTreeItem-content.Mui-selected .MuiTreeItem-label": {
                color: "#0c66e4 !important",
              },
              "& .MuiTreeItem-root .MuiTreeItem-content .MuiTreeItem-label .MuiTypography-root": {
                fontSize: "14px !important"
              }
            }}
          >
            {keywordsFilterMenuResult.map((item) => renderTree(item))}
          </TreeView>
        </Collapse>
      </List>
    </Box>
  );
};

export default SideMenu;
